import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IFilterAngularComp } from 'ag-grid-angular';
import { IFilterParams, IDoesFilterPassParams } from 'ag-grid-community';
import { DatePresetKey, DATE_PRESET_LABEL_KEYS, rangeForDatePreset } from '../utils/date-preset.utils';

// Legacy hunting-date presets (game records "Erlegungsdatum"), kept for
// backward compatibility with existing saved filter models / MapThemes.
type LegacyPreset = 'heute' | 'last7' | 'last30' | 'currentJagdjahr' | 'prevJagdjahr';
type Preset = LegacyPreset | DatePresetKey | 'custom' | null;

const LEGACY_PRESET_LABEL_KEYS: Record<LegacyPreset, string> = {
  heute:           'filter_date_range_items.today',
  last7:           'filter_date_range_items.last_7_days',
  last30:          'filter_date_range_items.last_30_days',
  currentJagdjahr: 'filter_date_range_items.current_year',
  prevJagdjahr:    'filter_date_range_items.last_year',
};

const DEFAULT_LEGACY_PRESETS: Preset[] = ['heute', 'last7', 'last30', 'currentJagdjahr', 'prevJagdjahr', 'custom'];

interface DateRange {
  from: Date | null;
  to:   Date | null;
}

export interface DatePresetFilterModel {
  preset: Preset;
  from:   string | null;
  to:     string | null;
}

export interface DatePresetFilterParams extends IFilterParams {
  /** Preset keys to offer, in order. Defaults to the legacy hunting-date set. */
  presets?: Preset[];
}

/**
 * Generic ag-grid date filter offering a dropdown of preset ranges
 * (today, this week, current month, ...) plus a custom from/to option.
 *
 * Originally built for game records' "Erlegungsdatum" (the legacy
 * heute/last7/last30/currentJagdjahr/prevJagdjahr presets, still the
 * default when no `presets` filterParams are given). WorkOrders and
 * GeoObjects pass the newer today/tomorrow/.../nextMonth set explicitly.
 */
@Component({
  selector: 'app-date-preset-filter',
  template: `
    <div class="ag-filter">
      <div class="ag-filter-body-wrapper">

        <div class="ag-input-field" style="padding: 8px 12px;">
          <div class="ag-wrapper ag-input-wrapper">
            <select
              class="ag-input-field-input"
              [(ngModel)]="pendingPreset"
              (change)="onSelectChange()"
            >
              <option [ngValue]="null">— Zeitraum wählen —</option>
              <option *ngFor="let p of presets" [value]="p.key">{{ p.label }}</option>
            </select>
          </div>
        </div>

        <div *ngIf="pendingPreset === 'custom'" class="ag-input-field" style="padding: 0 12px 8px; display: flex; flex-direction: column; gap: 4px;">
          <div class="w-100">
            <div class="ag-wrapper ag-input-wrapper ag-text-field-input-wrapper">
              <label class="dpf-label">Von</label>
              <input type="date" class="ag-input-field-input ag-text-field-input"
                [(ngModel)]="customFrom" (change)="onCustomChange()" />
            </div>
          </div>
          <div class="w-100">
            <div class="ag-wrapper ag-input-wrapper ag-text-field-input-wrapper">
              <label class="dpf-label">Bis</label>
              <input type="date" class="ag-input-field-input ag-text-field-input"
                [(ngModel)]="customTo" (change)="onCustomChange()" />
            </div>
          </div>
        </div>

        <div *ngIf="pendingRange.from && pendingPreset !== 'custom'" class="dpf-preview">
          {{ pendingRange.from | date:'dd.MM.yyyy' }}
          <ng-container *ngIf="pendingRange.to && !isSameDay(pendingRange.from, pendingRange.to)">
            &nbsp;–&nbsp;{{ pendingRange.to | date:'dd.MM.yyyy' }}
          </ng-container>
        </div>

      </div>

      <div class="ag-filter-apply-panel">
        <button
          class="ag-standard-button ag-filter-apply-panel-button"
          (click)="applyFilter()"
          [disabled]="!canApply()">
          Anwenden
        </button>
        <button
          class="ag-standard-button ag-filter-apply-panel-button"
          (click)="reset()"
          [disabled]="!isFilterActive() && !pendingPreset">
          Zurücksetzen
        </button>
      </div>
    </div>
  `,
  styles: [`
    .dpf-label {
      font-size: 14px;
      margin-bottom: 0;
      padding-right: 10px;
      padding-left:5px;
      margin-top: 10px;
      width: 20%;
    }
    .dpf-preview {
      font-size: 14px;
      color: var(--ag-alpine-active-color, #1976d2);
      background: #e3f2fd;
      border-radius: 4px;
      padding: 4px 10px;
      margin: 0 12px 8px;
    }
    .ag-input-field-input{
      font-size:14px;
      padding:5px;
      border-radius: 5px;
      margin-top: 10px;
    }
  `]
})
export class DatePresetFilterComponent implements IFilterAngularComp {

  private params!: DatePresetFilterParams;

  pendingPreset: Preset = null;
  pendingRange: DateRange = { from: null, to: null };
  selectedPreset: Preset = null;
  range: DateRange = { from: null, to: null };
  customFrom = '';
  customTo   = '';

  presets: { key: Preset; label: string }[] = [];

  constructor(private translate: TranslateService) {}

  agInit(params: DatePresetFilterParams): void {
    this.params = params;
    const keys = params.presets?.length ? params.presets : DEFAULT_LEGACY_PRESETS;
    this.presets = keys.map(key => ({ key, label: this.translate.instant(this.labelKeyFor(key)) }));
  }

  private labelKeyFor(preset: Preset): string {
    if (preset === 'custom') return 'filter_date_range_items.custom';
    if (preset && preset in LEGACY_PRESET_LABEL_KEYS) return LEGACY_PRESET_LABEL_KEYS[preset as LegacyPreset];
    if (preset && preset in DATE_PRESET_LABEL_KEYS) return DATE_PRESET_LABEL_KEYS[preset as DatePresetKey];
    return '';
  }

  isFilterActive(): boolean {
    if (this.selectedPreset === 'custom') {
      return !!(this.range.from && this.range.to);
    }
    return this.selectedPreset !== null;
  }

  canApply(): boolean {
    if (!this.pendingPreset) return false;
    if (this.pendingPreset === 'custom') {
      return !!(this.pendingRange.from && this.pendingRange.to);
    }
    return true;
  }

  doesFilterPass(params: IDoesFilterPassParams): boolean {
    const field = this.params.colDef.field as string;
    const raw   = this.params.getValue
      ? this.params.getValue(params.node)
      : params.node.data?.[field];
    const date  = this.toDate(raw);
    if (!date || !this.range.from) return false;

    const cell = this.startOfDay(date);
    const from = this.startOfDay(this.range.from);

    if (this.range.to) {
      const to = this.startOfDay(this.range.to);
      return cell >= from && cell <= to;
    }
    return cell.getTime() === from.getTime();
  }

  getModel(): DatePresetFilterModel | null {
    if (!this.isFilterActive()) return null;
    return {
      preset: this.selectedPreset,
      from:   this.range.from?.toISOString() ?? null,
      to:     this.range.to?.toISOString()   ?? null,
    };
  }

  setModel(model: any): void {
    if (!model) { this.reset(false); return; }
    this.selectedPreset = model.preset;
    this.pendingPreset  = model.preset;
    this.range = {
      from: model.from ? new Date(model.from) : null,
      to:   model.to   ? new Date(model.to)   : null,
    };
    this.pendingRange = { ...this.range };
    if (model.preset === 'custom') {
      this.customFrom = model.from ? model.from.substring(0, 10) : '';
      this.customTo   = model.to   ? model.to.substring(0, 10)   : '';
    }
  }

  onSelectChange(): void {
    if (this.pendingPreset && this.pendingPreset !== 'custom') {
      this.pendingRange = this.getRangeForPreset(this.pendingPreset);
      this.customFrom = '';
      this.customTo   = '';
    } else {
      this.pendingRange = { from: null, to: null };
    }
  }

  selectPreset(preset: Preset): void {
    if (this.pendingPreset === preset && preset !== 'custom') {
      this.pendingPreset = null;
      this.pendingRange  = { from: null, to: null };
      return;
    }
    this.pendingPreset = preset;
    if (preset !== 'custom') {
      this.pendingRange = this.getRangeForPreset(preset);
    } else {
      this.pendingRange = { from: null, to: null };
    }
  }

  onCustomChange(): void {
    if (this.customFrom && this.customTo) {
      const from = new Date(this.customFrom);
      const to   = new Date(this.customTo);
      if (from <= to) {
        this.pendingRange = { from, to };
      }
    }
  }

  applyFilter(): void {
    this.selectedPreset = this.pendingPreset;
    this.range          = { ...this.pendingRange };
    this.params.filterChangedCallback();
  }

  reset(notify = true): void {
    this.selectedPreset = null;
    this.pendingPreset  = null;
    this.range          = { from: null, to: null };
    this.pendingRange   = { from: null, to: null };
    this.customFrom     = '';
    this.customTo       = '';
    if (notify) this.params.filterChangedCallback();
  }

  isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth()    === b.getMonth()
        && a.getDate()     === b.getDate();
  }

  private startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private getRangeForPreset(preset: Preset): DateRange {
    if (!preset || preset === 'custom') return { from: null, to: null };

    if (preset in DATE_PRESET_LABEL_KEYS) {
      const range = rangeForDatePreset(preset as DatePresetKey);
      return { from: range.from, to: range.to };
    }

    const today = new Date();
    switch (preset as LegacyPreset) {
      case 'heute':
        return { from: this.startOfDay(today), to: this.startOfDay(today) };

      case 'last7': {
        const from = new Date(today);
        from.setDate(from.getDate() - 6);
        return { from: this.startOfDay(from), to: this.startOfDay(today) };
      }

      case 'last30': {
        const from = new Date(today);
        from.setDate(from.getDate() - 29);
        return { from: this.startOfDay(from), to: this.startOfDay(today) };
      }

      case 'currentJagdjahr': {
        const startYear = today.getMonth() >= 3
          ? today.getFullYear()
          : today.getFullYear() - 1;
        return {
          from: new Date(startYear,     3,  1),
          to:   new Date(startYear + 1, 2, 31),
        };
      }

      case 'prevJagdjahr': {
        const startYear = today.getMonth() >= 3
          ? today.getFullYear() - 1
          : today.getFullYear() - 2;
        return {
          from: new Date(startYear,     3,  1),
          to:   new Date(startYear + 1, 2, 31),
        };
      }

      default:
        return { from: null, to: null };
    }
  }

  private toDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value.toDate === 'function') return value.toDate();
    if (value?.seconds)  return new Date(value.seconds  * 1000);
    if (value?._seconds) return new Date(value._seconds * 1000);
    if (typeof value === 'string' || typeof value === 'number') return new Date(value);
    return null;
  }
}
